import { Check, Copy, Shield } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '../../utils/authcontextt';
import { Button, Alert } from '../ui';

import { MfaCodeForm } from './MfaCodeForm';

const CODE_FORM_COPY = {
  label: 'Verifieringskod',
  help: 'Ange den sexsiffriga koden från din autentiseringsapp',
  submit: 'Verifiera och aktivera',
  submitting: 'Verifierar...',
  back: 'Tillbaka',
};

interface MFASetupProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

type EnrolledFactor = {
  factorId: string;
  qr_code: string;
  secret: string;
};

export default function MFASetup({ onComplete, onCancel }: MFASetupProps) {
  const { setupMFA, activateMFA } = useAuth();
  const [factor, setFactor] = useState<EnrolledFactor | null>(null);
  const [error, setError] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleEnroll = async () => {
    setIsEnrolling(true);
    setError('');

    const result = await setupMFA();

    if (result.success && result.factorId && result.qr_code && result.secret) {
      setFactor({
        factorId: result.factorId,
        qr_code: result.qr_code,
        secret: result.secret,
      });
    } else {
      setError(result.error || 'Kunde inte starta uppsättningen');
    }

    setIsEnrolling(false);
  };

  const handleActivate = async (code: string) => {
    if (!factor) return;

    setIsActivating(true);
    setError('');

    const result = await activateMFA({ factorId: factor.factorId, code });

    if (result.success) {
      onComplete?.();
    } else {
      setError(result.error || 'Fel kod');
    }

    setIsActivating(false);
  };

  const copySecret = () => {
    if (!factor) return;
    navigator.clipboard.writeText(factor.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!factor) {
    return (
      <div className="max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-blue to-brand-secondary-blue rounded-full mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-brand-blue mb-2">
            Aktivera tvåfaktorsautentisering
          </h2>
          <p className="text-brand-blue/70">
            Öka säkerheten för ditt konto med tvåfaktorsautentisering (2FA)
          </p>
        </div>

        {error && (
          <div className="mb-4">
            <Alert type="error" message={error} />
          </div>
        )}

        <div className="space-y-4 mb-6">
          <div className="bg-brand-beige-tint-1/30 rounded-lg p-4">
            <h3 className="font-semibold text-brand-blue mb-2">Vad behöver du?</h3>
            <ul className="text-sm text-brand-blue/70 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  En autentiseringsapp (Google Authenticator, Authy, Microsoft Authenticator)
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Din mobiltelefon för att skanna QR-koden</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex gap-3">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={isEnrolling} className="flex-1">
              Avbryt
            </Button>
          )}
          <Button variant="primary" onClick={handleEnroll} loading={isEnrolling} className="flex-1">
            {isEnrolling ? 'Genererar...' : 'Fortsätt'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-blue to-brand-secondary-blue rounded-full mb-4">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-brand-blue mb-2">Skanna QR-koden</h2>
        <p className="text-brand-blue/70">
          Använd din autentiseringsapp för att skanna koden nedan
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert type="error" message={error} />
        </div>
      )}

      <div className="mb-6">
        <div className="bg-white p-4 rounded-lg border-2 border-brand-blue/10 mb-4">
          <img src={factor.qr_code} alt="QR Code for 2FA setup" className="w-full h-auto" />
        </div>

        <div className="bg-brand-beige-tint-1/30 rounded-lg p-4">
          <p className="text-xs text-brand-blue/70 mb-2 font-medium">
            Kan du inte skanna koden? Ange den här nyckeln manuellt:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white px-3 py-2 rounded text-sm font-mono text-brand-blue border border-brand-blue/20">
              {factor.secret}
            </code>
            <button
              type="button"
              onClick={copySecret}
              className="p-2 hover:bg-white rounded transition-colors"
              title="Kopiera nyckel"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-brand-blue/70" />
              )}
            </button>
          </div>
        </div>
      </div>

      <MfaCodeForm
        inputId="mfa-activate"
        copy={CODE_FORM_COPY}
        isSubmitting={isActivating}
        onSubmit={handleActivate}
        onBack={() => {
          setFactor(null);
          setError('');
        }}
        onDirty={() => setError('')}
      />
    </div>
  );
}
